import { useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProjectBlockerDiscussions, createProjectBlockerDiscussion, updateProjectBlockerDiscussion, deleteProjectBlockerDiscussion } from '@services/projects.service';
import { getRealtimeSocket, subscribeProject, unsubscribeProject } from '@services/realtime.socket';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_BLOCKERS_VIEW, DISCUSSION_SOCKET_EVENTS } from './constants/discussion.constants';
import { BLOCKER_DISCUSSIONS_QUERY_KEY } from './constants/discussion.queryKeys';
import { getBlockerIdFromDiscussionSocketPayload } from './utils/discussionSocketPayload.utils';
import { sortByNewestFirst } from './utils/discussionSort.utils';

// payload parsing helpers live in utils/

/**
 * Blocker discussion: list of posts, create post. Same payload style as project discussion
 * (JSON when no files, FormData with content + attachmentUrls + files when files attached).
 */
export function useBlockerDiscussions(projectId, blockerId, taskId = null) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canViewBlockers = can(PROJECT_BLOCKERS_VIEW);

    useEffect(() => {
        if (!projectId || !blockerId || !canViewBlockers) return;
        const socket = getRealtimeSocket();
        if (!socket) return;

        const handleNewPost = (data) => {
            const targetBlockerId = getBlockerIdFromDiscussionSocketPayload(data);
            if (targetBlockerId == null || String(targetBlockerId) !== String(blockerId)) return;
            const msg = data.message;
            if (!msg) return;

            queryClient.setQueryData(BLOCKER_DISCUSSIONS_QUERY_KEY(projectId, blockerId, taskId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                if (list.some((p) => (p.id ?? p.discussionId) === msg.id)) return prev;
                return sortByNewestFirst([...list, msg]);
            });
        };

        const handleUpdatePost = (data) => {
            const targetBlockerId = getBlockerIdFromDiscussionSocketPayload(data);
            if (targetBlockerId == null || String(targetBlockerId) !== String(blockerId)) return;
            const msg = data.message;
            if (!msg) return;
            queryClient.setQueryData(BLOCKER_DISCUSSIONS_QUERY_KEY(projectId, blockerId, taskId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.map((p) => ((p.id ?? p.discussionId) === msg.id ? { ...p, ...msg } : p));
            });
        };

        const handleDeletePost = (data) => {
            const targetBlockerId = getBlockerIdFromDiscussionSocketPayload(data);
            if (targetBlockerId == null || String(targetBlockerId) !== String(blockerId)) return;
            const id = data.messageId;
            if (id == null) return;
            queryClient.setQueryData(BLOCKER_DISCUSSIONS_QUERY_KEY(projectId, blockerId, taskId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.filter((p) => (p.id ?? p.discussionId) !== id);
            });
        };

        socket.on(DISCUSSION_SOCKET_EVENTS.TASK_BLOCKER_NEW, handleNewPost);
        socket.on(DISCUSSION_SOCKET_EVENTS.TASK_BLOCKER_UPDATED, handleUpdatePost);
        socket.on(DISCUSSION_SOCKET_EVENTS.TASK_BLOCKER_DELETED, handleDeletePost);

        socket.on(DISCUSSION_SOCKET_EVENTS.BLOCKER_NEW, handleNewPost);
        socket.on(DISCUSSION_SOCKET_EVENTS.BLOCKER_UPDATED, handleUpdatePost);
        socket.on(DISCUSSION_SOCKET_EVENTS.BLOCKER_DELETED, handleDeletePost);

        subscribeProject(projectId);

        return () => {
            socket.off(DISCUSSION_SOCKET_EVENTS.TASK_BLOCKER_NEW, handleNewPost);
            socket.off(DISCUSSION_SOCKET_EVENTS.TASK_BLOCKER_UPDATED, handleUpdatePost);
            socket.off(DISCUSSION_SOCKET_EVENTS.TASK_BLOCKER_DELETED, handleDeletePost);

            socket.off(DISCUSSION_SOCKET_EVENTS.BLOCKER_NEW, handleNewPost);
            socket.off(DISCUSSION_SOCKET_EVENTS.BLOCKER_UPDATED, handleUpdatePost);
            socket.off(DISCUSSION_SOCKET_EVENTS.BLOCKER_DELETED, handleDeletePost);

            unsubscribeProject(projectId);
        };
    }, [projectId, blockerId, taskId, queryClient, canViewBlockers]);

    const {
        data: discussions = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: BLOCKER_DISCUSSIONS_QUERY_KEY(projectId, blockerId, taskId),
        queryFn: () => getProjectBlockerDiscussions(projectId, blockerId, taskId),
        enabled: !!projectId && !!blockerId && canViewBlockers,
        // Blocker discussion stays fresh via sockets; prevent noisy refetches on reopen.
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        // Fetch on first open and when stale; skip noisy remount refetches while fresh.
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const discussionsNewestFirst = useMemo(
        () => sortByNewestFirst(Array.isArray(discussions) ? discussions : []),
        [discussions],
    );

    const createMutation = useMutation({
        mutationFn: (payload) => createProjectBlockerDiscussion(projectId, blockerId, payload, taskId),
        onSuccess: (data) => {
            const post = data?.data ?? data;
            if (post && (post.id != null || post.discussionId != null)) {
                queryClient.setQueryData(BLOCKER_DISCUSSIONS_QUERY_KEY(projectId, blockerId, taskId), (prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    const id = post.id ?? post.discussionId;
                    if (list.some((p) => (p.id ?? p.discussionId) === id)) return prev;
                    return sortByNewestFirst([...list, post]);
                });
            }
        },
    });

    const createPost = (body, callbacks = {}) => {
        const { content, attachments = [], files } = body;
        const linkAttachments = Array.isArray(attachments) ? attachments : [];
        let payload;
        if (files?.length) {
            const formData = new FormData();
            formData.append('content', typeof content === 'string' ? content : '');
            if (linkAttachments.length > 0) {
                formData.append('attachmentUrls', JSON.stringify(linkAttachments));
            }
            files.forEach((file) => formData.append('files', file));
            payload = formData;
        } else {
            payload = {
                content: typeof content === 'string' ? content : '',
                attachments: linkAttachments,
            };
        }
        createMutation.mutate(payload, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const editMutation = useMutation({
        mutationFn: ({ discussionId, payload }) => updateProjectBlockerDiscussion(projectId, blockerId, discussionId, payload, taskId),
        onSuccess: (data, { discussionId }) => {
            const post = data?.data ?? data;
            queryClient.setQueryData(BLOCKER_DISCUSSIONS_QUERY_KEY(projectId, blockerId, taskId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.map((p) => ((p.id ?? p.discussionId) === discussionId ? { ...p, ...post } : p));
            });
        },
    });

    const editPost = (discussionId, body, callbacks = {}) => {
        const { content, attachments = [], files } = body;
        const linkAttachments = Array.isArray(attachments) ? attachments : [];
        let payload;
        if (files?.length) {
            const formData = new FormData();
            formData.append('content', typeof content === 'string' ? content : '');
            if (linkAttachments.length > 0) {
                formData.append('attachmentUrls', JSON.stringify(linkAttachments));
            }
            files.forEach((file) => formData.append('files', file));
            payload = formData;
        } else {
            payload = {
                content: typeof content === 'string' ? content : '',
                attachments: linkAttachments,
            };
        }
        editMutation.mutate({ discussionId, payload }, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const deleteMutation = useMutation({
        mutationFn: (discussionId) => deleteProjectBlockerDiscussion(projectId, blockerId, discussionId, taskId),
        onSuccess: (_, discussionId) => {
            queryClient.setQueryData(BLOCKER_DISCUSSIONS_QUERY_KEY(projectId, blockerId, taskId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.filter((p) => (p.id ?? p.discussionId) !== discussionId);
            });
        },
    });

    const deletePost = (discussionId, callbacks = {}) => {
        deleteMutation.mutate(discussionId, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    return {
        discussions: discussionsNewestFirst,
        isLoading,
        isError,
        refetch,
        createPost,
        createPostPending: createMutation.isPending,
        editPost,
        editPostPending: editMutation.isPending,
        deletePost,
        deletePostPending: deleteMutation.isPending,
    };
}

